import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import ProtectedRoute from "../../hooks/ProtectedRoute";
import RouteChangeListener from "./RouteChangeListener";
import GlobalLoadingOverlay from "../../features/common/components/GlobalLoadingOverlay";
import EditPetPage from "../../pages/user_profile/EditPetPate";
import CreatePetPage from "../../pages/user_profile/CreatePetPage";
import ViewPetPage from "../../pages/user_profile/ViewPetPage";

const ApplicationPage = lazy(
  () => import("../../pages/sitter_profile/ApplicationPage"),
);
const LoginPage = lazy(() => import("../../pages/auth/LoginPage"));
const HomePage = lazy(() => import("../../pages/HomePage"));
const ForgotPasswordPage = lazy(
  () => import("../../pages/auth/ForgotPasswordPage"),
);
const SignupPage = lazy(() => import("../../pages/auth/SignupPage"));
const SignupAccountPage = lazy(
  () => import("../../pages/auth/SignupAccountPage"),
);
const SearchPage = lazy(() => import("../../pages/SearchPage"));
const SitterProfilePage = lazy(() => import("../../pages/SitterProfilePage"));
const AppLayout = lazy(() => import("../Layout/AppLayout"));
const DashboardPage = lazy(() => import("../../pages/dashboard/DashboardPage"));
const MessagesPage = lazy(() => import("../../pages/MessagesPage"));
const CalendarPage = lazy(() => import("../../pages/CalendarPage"));
const AboutMePage = lazy(() => import("../../pages/user_profile/AboutMePage"));
const ServicesPage = lazy(
  () => import("../../pages/sitter_profile/ServicesPage"),
);
const DashboardLayout = lazy(() => import("../Layout/DashboardLayout"));
const ProfileLayout = lazy(() => import("../Layout/ProfileLayout"));
const FeedbackPage = lazy(() => import("../../pages/dashboard/FeedbackPage"));
const MyAddressPage = lazy(
  () => import("../../pages/user_profile/AddressPage"),
);
const SitterAddressPage = lazy(
  () => import("../../pages/sitter_profile/AddressPage"),
);
const HeadlinePage = lazy(
  () => import("../../pages/sitter_profile/HeadlinePage"),
);
const ProfileGalleryPage = lazy(
  () => import("../../pages/sitter_profile/ProfileGalleryPage"),
);
const NotificationPage = lazy(
  () => import("../../pages/user_profile/NotificationPage"),
);
const DeletePage = lazy(() => import("../../pages/user_profile/DeletePage"));
const PasswordPage = lazy(
  () => import("../../pages/user_profile/PasswordPage"),
);
const PhoneVerificationPage = lazy(
  () => import("../../pages/user_profile/PhoneVerificationPage"),
);
const ExperiencePage = lazy(
  () => import("../../pages/sitter_profile/ExperiencePage"),
);
const MyPetsPage = lazy(() => import("../../pages/user_profile/MyPetsPage"));
const PayoutPage = lazy(() => import("../../pages/sitter_profile/PayoutPage"));
const CompleteProfilePage = lazy(
  () => import("../../pages/sitter_profile/CompleteProfilePage"),
);
const FavoritesPage = lazy(() => import("../../pages/dashboard/FavoritesPage"));
const AuthMyInfoVerificationPage = lazy(
  () => import("../../pages/auth/MyInfoVerificationPage"),
);
const SitterMyInfoVerificationPage = lazy(
  () => import("../../pages/sitter_profile/MyInfoVerificationPage"),
);
const VerifyEmailPage = lazy(() => import("../../pages/auth/VerifyEmailPage"));
const ResetPasswordPage = lazy(
  () => import("../../pages/auth/ResetPasswordPage"),
);

const AppRoute = () => {
  return (
    <Suspense
    // fallback={
    //   <Loading />
    // }
    >
      {/* Global level route change indicator need these two components */}
      <RouteChangeListener />
      <GlobalLoadingOverlay />

      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/sign-up" element={<SignupPage />} />
        <Route path="/sign-up/account" element={<SignupAccountPage />} />
        <Route
          path="/sign-up/verification"
          element={<AuthMyInfoVerificationPage />}
        />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        <Route element={<AppLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/sitters/:userId" element={<SitterProfilePage />} />

          {/* user routes */}
          <Route element={<ProtectedRoute />}>
            <Route path="/sitter/application" element={<ApplicationPage />} />
            <Route
              path="/sitter/application/verification"
              element={<SitterMyInfoVerificationPage />}
            />

            <Route element={<DashboardLayout />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/dashboard/feedback" element={<FeedbackPage />} />
              <Route path="/dashboard/favorites" element={<FavoritesPage />} />
              <Route path="/messages" element={<MessagesPage />} />

              {/* Profile Routes */}
              <Route element={<ProfileLayout />}>
                <Route
                  path="/profile"
                  element={<Navigate to="/profile/about-me" replace />}
                />

                <Route path="/profile/about-me" element={<AboutMePage />} />
                <Route path="/profile/address" element={<MyAddressPage />} />
                <Route
                  path="/profile/phone"
                  element={<PhoneVerificationPage />}
                />
                <Route
                  path="/profile/notification"
                  element={<NotificationPage />}
                />
                <Route path="/profile/delete" element={<DeletePage />} />
                <Route path="/profile/password" element={<PasswordPage />} />
                <Route path="/profile/my-pets" element={<MyPetsPage />}>
                  <Route path="view/:pet_id" element={<ViewPetPage />} />
                  <Route path="create" element={<CreatePetPage />} />
                  <Route path="edit/:pet_id" element={<EditPetPage />} />
                </Route>
              </Route>
            </Route>
          </Route>
          {/* Sitter Routes */}
          <Route element={<ProtectedRoute sitterOnly />}>
            <Route element={<DashboardLayout />}>
              <Route path="/profile/availability" element={<CalendarPage />} />
              <Route path="/profile/services" element={<ServicesPage />} />

              <Route element={<ProfileLayout />}>
                <Route
                  path="/sitter/complete-profile"
                  element={<CompleteProfilePage />}
                />
                <Route
                  path="/sitter"
                  element={<Navigate to="/sitter/address" replace />}
                />
                <Route path="/sitter/address" element={<SitterAddressPage />} />
                <Route path="/sitter/headline" element={<HeadlinePage />} />
                <Route
                  path="/sitter/photo-gallery"
                  element={<ProfileGalleryPage />}
                />
                <Route path="/sitter/experience" element={<ExperiencePage />} />
                <Route path="/sitter/payout-method" element={<PayoutPage />} />
              </Route>
            </Route>
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
};

export default AppRoute;
